import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import toast from 'react-hot-toast';
import {
  Settings,
  Save,
  RefreshCw,
  CreditCard,
  Shield,
  Globe,
  AlertTriangle,
  CheckCircle,
  ToggleLeft,
  ToggleRight,
  BarChart3,
  Plus,
  Trash2,
  X
} from 'lucide-react';

interface SystemConfig {
  // Modo mantenimiento
  maintenanceMode: boolean;
  maintenanceMessage: string;

  // Planes
  plans: {
    free: {
      name: string;
      maxUsers: number;
      maxInstructors: number;
      features: string[];
      hasAdvancedStats: boolean;
      maxRooms: number;
      hasCommunications: boolean;
      hasOnlinePayments: boolean;
    };
    basic: {
      name: string;
      price: number;
      maxUsers: number;
      maxInstructors: number;
      features: string[];
      hasAdvancedStats: boolean;
      maxRooms: number;
      hasCommunications: boolean;
      hasOnlinePayments: boolean;
    };
    pro: {
      name: string;
      price: number;
      maxUsers: number;
      maxInstructors: number;
      features: string[];
      hasAdvancedStats: boolean;
      maxRooms: number;
      hasCommunications: boolean;
      hasOnlinePayments: boolean;
    };
  };

  // Stripe
  platformFeePercent: number;

  // General
  supportEmail: string;
  defaultLanguage: string;
  defaultTimezone: string;
}

const defaultConfig: SystemConfig = {
  maintenanceMode: false,
  maintenanceMessage: 'Estamos realizando tareas de mantenimiento. Volvemos pronto.',
  plans: {
    free: {
      name: 'Gratis',
      maxUsers: 50,
      maxInstructors: 1,
      features: ['Hasta 50 usuarios', '1 instructor', 'Funciones básicas'],
      hasAdvancedStats: false,
      maxRooms: 1,
      hasCommunications: false,
      hasOnlinePayments: false
    },
    basic: {
      name: 'Básico',
      price: 19,
      maxUsers: 200,
      maxInstructors: 5,
      features: ['Hasta 200 usuarios', '5 instructores', 'Estadísticas básicas'],
      hasAdvancedStats: false,
      maxRooms: 2,
      hasCommunications: true,
      hasOnlinePayments: true
    },
    pro: {
      name: 'Profesional',
      price: 49,
      maxUsers: -1,
      maxInstructors: -1,
      features: ['Usuarios ilimitados', 'Instructores ilimitados', 'Estadísticas avanzadas', 'Soporte prioritario'],
      hasAdvancedStats: true,
      maxRooms: -1,
      hasCommunications: true,
      hasOnlinePayments: true
    }
  },
  platformFeePercent: 5,
  supportEmail: 'navora.citas@gmail.com',
  defaultLanguage: 'es',
  defaultTimezone: 'Europe/Madrid'
};

const SuperAdminConfigPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<SystemConfig>(defaultConfig);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const configDoc = await getDoc(doc(db, 'system', 'config'));
      if (configDoc.exists()) {
        // Merge with default config to ensure new fields exist
        const data = configDoc.data();
        const mergedConfig = {
          ...defaultConfig,
          ...data,
          plans: {
            free: { ...defaultConfig.plans.free, ...data.plans?.free },
            basic: { ...defaultConfig.plans.basic, ...data.plans?.basic },
            pro: { ...defaultConfig.plans.pro, ...data.plans?.pro }
          }
        };
        setConfig(mergedConfig as SystemConfig);
      }
    } catch (e) {
      console.error('Error cargando configuración:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'system', 'config'), {
        ...config,
        updatedAt: new Date()
      });
      toast.success('Configuración guardada');
      setHasChanges(false);
    } catch (e: any) {
      console.error('Error guardando:', e);
      toast.error('Error guardando la configuración');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (updates: Partial<SystemConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const updatePlan = (plan: 'free' | 'basic' | 'pro', updates: any) => {
    setConfig(prev => ({
      ...prev,
      plans: {
        ...prev.plans,
        [plan]: { ...prev.plans[plan], ...updates }
      }
    }));
    setHasChanges(true);
  };

  // Funciones para gestionar features
  const addFeature = (plan: 'free' | 'basic' | 'pro') => {
    const currentFeatures = config.plans[plan].features || [];
    updatePlan(plan, { features: [...currentFeatures, 'Nueva característica'] });
  };

  const removeFeature = (plan: 'free' | 'basic' | 'pro', index: number) => {
    const currentFeatures = [...config.plans[plan].features];
    currentFeatures.splice(index, 1);
    updatePlan(plan, { features: currentFeatures });
  };

  const updateFeature = (plan: 'free' | 'basic' | 'pro', index: number, value: string) => {
    const currentFeatures = [...config.plans[plan].features];
    currentFeatures[index] = value;
    updatePlan(plan, { features: currentFeatures });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Settings className="w-7 h-7 text-amber-500" />
            Configuración del Sistema
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Parámetros globales de la plataforma
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Guardar Cambios
        </button>
      </div>

      {hasChanges && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Tienes cambios sin guardar
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Modo Mantenimiento */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-500" />
              Modo Mantenimiento
            </h2>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Activar modo mantenimiento</p>
                <p className="text-sm text-gray-500">Los usuarios verán un mensaje de mantenimiento</p>
              </div>
              <button
                onClick={() => updateConfig({ maintenanceMode: !config.maintenanceMode })}
                className={`p-2 rounded-lg transition-colors ${config.maintenanceMode
                  ? 'bg-red-100 text-red-600'
                  : 'bg-gray-100 text-gray-400'
                  }`}
              >
                {config.maintenanceMode ? (
                  <ToggleRight className="w-8 h-8" />
                ) : (
                  <ToggleLeft className="w-8 h-8" />
                )}
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mensaje de mantenimiento
              </label>
              <textarea
                value={config.maintenanceMessage}
                onChange={(e) => updateConfig({ maintenanceMessage: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
              />
            </div>
          </div>
        </div>

        {/* Configuración de Stripe */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-purple-500" />
              Configuración de Pagos
            </h2>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Comisión de plataforma (%)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={config.platformFeePercent}
                  onChange={(e) => updateConfig({ platformFeePercent: parseFloat(e.target.value) || 0 })}
                  min="0"
                  max="30"
                  step="0.5"
                  className="w-24 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-center"
                />
                <span className="text-gray-500">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Porcentaje que se cobra en cada transacción
              </p>
            </div>
          </div>
        </div>

        {/* Configuración General */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-500" />
              Configuración General
            </h2>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email de soporte
              </label>
              <input
                type="email"
                value={config.supportEmail}
                onChange={(e) => updateConfig({ supportEmail: e.target.value })}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Idioma por defecto
                </label>
                <select
                  value={config.defaultLanguage}
                  onChange={(e) => updateConfig({ defaultLanguage: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg"
                >
                  <option value="es">Español</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Zona horaria
                </label>
                <select
                  value={config.defaultTimezone}
                  onChange={(e) => updateConfig({ defaultTimezone: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg"
                >
                  <option value="Europe/Madrid">Europe/Madrid</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="America/New_York">America/New_York</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Configuración de Planes Completa */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 lg:col-span-2">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-500" />
              Configuración de Planes
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Configura los nombres, límites y características de cada plan
            </p>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

              {/* Plan Gratis */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <input
                    type="text"
                    value={config.plans.free.name}
                    onChange={(e) => updatePlan('free', { name: e.target.value })}
                    className="font-semibold text-gray-900 dark:text-white bg-transparent border-b border-transparent hover:border-gray-300 focus:border-amber-500 focus:outline-none w-full"
                  />
                  <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full whitespace-nowrap ml-2">
                    Sin costo
                  </span>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Máx. Usuarios</label>
                    <input
                      type="number"
                      value={config.plans.free.maxUsers}
                      onChange={(e) => updatePlan('free', { maxUsers: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                      placeholder="-1 = ilimitado"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Máx. Instructores</label>
                    <input
                      type="number"
                      value={config.plans.free.maxInstructors}
                      onChange={(e) => updatePlan('free', { maxInstructors: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                      placeholder="-1 = ilimitado"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Máx. Salas</label>
                    <input
                      type="number"
                      value={config.plans.free.maxRooms}
                      onChange={(e) => updatePlan('free', { maxRooms: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                      placeholder="-1 = ilimitado"
                    />
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Estadísticas</span>
                    <button
                      onClick={() => updatePlan('free', { hasAdvancedStats: !config.plans.free.hasAdvancedStats })}
                      className={`p-1 rounded-lg transition-colors ${config.plans.free.hasAdvancedStats
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-400'
                        }`}
                    >
                      {config.plans.free.hasAdvancedStats ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Comunicaciones</span>
                    <button
                      onClick={() => updatePlan('free', { hasCommunications: !config.plans.free.hasCommunications })}
                      className={`p-1 rounded-lg transition-colors ${config.plans.free.hasCommunications
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-400'
                        }`}
                    >
                      {config.plans.free.hasCommunications ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Pagos Online</span>
                    <button
                      onClick={() => updatePlan('free', { hasOnlinePayments: !config.plans.free.hasOnlinePayments })}
                      className={`p-1 rounded-lg transition-colors ${config.plans.free.hasOnlinePayments
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-400'
                        }`}
                    >
                      {config.plans.free.hasOnlinePayments ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  {/* Features Editor */}
                  <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                    <label className="block text-xs font-medium text-gray-500 mb-2">Características</label>
                    <div className="space-y-2">
                      {config.plans.free.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => updateFeature('free', idx, e.target.value)}
                            className="flex-1 px-2 py-1 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded"
                          />
                          <button
                            onClick={() => removeFeature('free', idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addFeature('free')}
                        className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 mt-2"
                      >
                        <Plus className="w-3 h-3" /> Añadir característica
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Plan Básico */}
              <div className="border border-amber-200 dark:border-amber-700 rounded-xl p-4 bg-amber-50/30 dark:bg-amber-900/10">
                <div className="flex items-center justify-between mb-4">
                  <input
                    type="text"
                    value={config.plans.basic.name}
                    onChange={(e) => updatePlan('basic', { name: e.target.value })}
                    className="font-semibold text-gray-900 dark:text-white bg-transparent border-b border-transparent hover:border-amber-300 focus:border-amber-500 focus:outline-none w-full"
                  />
                  <span className="text-xs px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-600 rounded-full whitespace-nowrap ml-2">
                    Pago
                  </span>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Precio (€/mes)</label>
                    <input
                      type="number"
                      value={config.plans.basic.price}
                      onChange={(e) => updatePlan('basic', { price: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Máx. Usuarios</label>
                    <input
                      type="number"
                      value={config.plans.basic.maxUsers}
                      onChange={(e) => updatePlan('basic', { maxUsers: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                      placeholder="-1 = ilimitado"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Máx. Instructores</label>
                    <input
                      type="number"
                      value={config.plans.basic.maxInstructors}
                      onChange={(e) => updatePlan('basic', { maxInstructors: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                      placeholder="-1 = ilimitado"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Máx. Salas</label>
                    <input
                      type="number"
                      value={config.plans.basic.maxRooms}
                      onChange={(e) => updatePlan('basic', { maxRooms: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                      placeholder="-1 = ilimitado"
                    />
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Estadísticas</span>
                    <button
                      onClick={() => updatePlan('basic', { hasAdvancedStats: !config.plans.basic.hasAdvancedStats })}
                      className={`p-1 rounded-lg transition-colors ${config.plans.basic.hasAdvancedStats
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-400'
                        }`}
                    >
                      {config.plans.basic.hasAdvancedStats ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Comunicaciones</span>
                    <button
                      onClick={() => updatePlan('basic', { hasCommunications: !config.plans.basic.hasCommunications })}
                      className={`p-1 rounded-lg transition-colors ${config.plans.basic.hasCommunications
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-400'
                        }`}
                    >
                      {config.plans.basic.hasCommunications ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Pagos Online</span>
                    <button
                      onClick={() => updatePlan('basic', { hasOnlinePayments: !config.plans.basic.hasOnlinePayments })}
                      className={`p-1 rounded-lg transition-colors ${config.plans.basic.hasOnlinePayments
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-400'
                        }`}
                    >
                      {config.plans.basic.hasOnlinePayments ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  {/* Features Editor */}
                  <div className="pt-3 border-t border-amber-200 dark:border-amber-700">
                    <label className="block text-xs font-medium text-gray-500 mb-2">Características</label>
                    <div className="space-y-2">
                      {config.plans.basic.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => updateFeature('basic', idx, e.target.value)}
                            className="flex-1 px-2 py-1 text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded"
                          />
                          <button
                            onClick={() => removeFeature('basic', idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addFeature('basic')}
                        className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 mt-2"
                      >
                        <Plus className="w-3 h-3" /> Añadir característica
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Plan Pro */}
              <div className="border border-purple-200 dark:border-purple-700 rounded-xl p-4 bg-purple-50/30 dark:bg-purple-900/10">
                <div className="flex items-center justify-between mb-4">
                  <input
                    type="text"
                    value={config.plans.pro.name}
                    onChange={(e) => updatePlan('pro', { name: e.target.value })}
                    className="font-semibold text-gray-900 dark:text-white bg-transparent border-b border-transparent hover:border-purple-300 focus:border-purple-500 focus:outline-none w-full"
                  />
                  <span className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 rounded-full whitespace-nowrap ml-2">
                    Premium
                  </span>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Precio (€/mes)</label>
                    <input
                      type="number"
                      value={config.plans.pro.price}
                      onChange={(e) => updatePlan('pro', { price: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text.xs font-medium text-gray-500 mb-1">Máx. Usuarios</label>
                    <input
                      type="number"
                      value={config.plans.pro.maxUsers}
                      onChange={(e) => updatePlan('pro', { maxUsers: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                      placeholder="-1 = ilimitado"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Máx. Instructores</label>
                    <input
                      type="number"
                      value={config.plans.pro.maxInstructors}
                      onChange={(e) => updatePlan('pro', { maxInstructors: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                      placeholder="-1 = ilimitado"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Máx. Salas</label>
                    <input
                      type="number"
                      value={config.plans.pro.maxRooms}
                      onChange={(e) => updatePlan('pro', { maxRooms: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                      placeholder="-1 = ilimitado"
                    />
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Estadísticas</span>
                    <button
                      onClick={() => updatePlan('pro', { hasAdvancedStats: !config.plans.pro.hasAdvancedStats })}
                      className={`p-1 rounded-lg transition-colors ${config.plans.pro.hasAdvancedStats
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-400'
                        }`}
                    >
                      {config.plans.pro.hasAdvancedStats ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Comunicaciones</span>
                    <button
                      onClick={() => updatePlan('pro', { hasCommunications: !config.plans.pro.hasCommunications })}
                      className={`p-1 rounded-lg transition-colors ${config.plans.pro.hasCommunications
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-400'
                        }`}
                    >
                      {config.plans.pro.hasCommunications ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Pagos Online</span>
                    <button
                      onClick={() => updatePlan('pro', { hasOnlinePayments: !config.plans.pro.hasOnlinePayments })}
                      className={`p-1 rounded-lg transition-colors ${config.plans.pro.hasOnlinePayments
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-400'
                        }`}
                    >
                      {config.plans.pro.hasOnlinePayments ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  {/* Features Editor */}
                  <div className="pt-3 border-t border-purple-200 dark:border-purple-700">
                    <label className="block text-xs font-medium text-gray-500 mb-2">Características</label>
                    <div className="space-y-2">
                      {config.plans.pro.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => updateFeature('pro', idx, e.target.value)}
                            className="flex-1 px-2 py-1 text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded"
                          />
                          <button
                            onClick={() => removeFeature('pro', idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addFeature('pro')}
                        className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 mt-2"
                      >
                        <Plus className="w-3 h-3" /> Añadir característica
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <p className="text-xs text-gray-500 mt-4">
              💡 Usa -1 para indicar usuarios/instructores ilimitados. Los cambios se reflejarán en la página de planes del negocio.
            </p>
          </div>
        </div>

        {/* Info de estado */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Estado del Sistema
            </h2>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <span className="text-sm text-gray-600 dark:text-gray-300">Firebase</span>
              <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-full">
                Operativo
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <span className="text-sm text-gray-600 dark:text-gray-300">Cloud Functions</span>
              <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-full">
                Operativo
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <span className="text-sm text-gray-600 dark:text-gray-300">Stripe</span>
              <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-full">
                Conectado
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <span className="text-sm text-gray-600 dark:text-gray-300">Modo mantenimiento</span>
              <span className={`text-xs px-2 py-1 rounded-full ${config.maintenanceMode
                ? 'bg-red-100 dark:bg-red-900/30 text-red-600'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600'
                }`}>
                {config.maintenanceMode ? 'Activo' : 'Inactivo'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminConfigPage;










